import { stationOpsApi } from "../config/apiConfig";
import { CursorPage, EntityType, StatusPeriod } from "../types/alerting";

export type CurrentStatusSort = "newest" | "oldest";

interface CurrentStatusOptions {
  entityType: EntityType;
  status?: string | null;
  /** Matches station id or name; for EVSEs also the EVSE uid or evse_id. Blank means no search. */
  search?: string | null;
  /** Orders by when the current status began, across the whole result. */
  sort?: CurrentStatusSort;
  limit?: number;
  cursor?: string | null;
}

interface HistoryOptions {
  from?: string | null;
  to?: string | null;
  limit?: number;
  cursor?: string | null;
}

const toPeriod = (raw: any): StatusPeriod => ({
  entityType: raw.entity_type,
  entityId: raw.entity_id,
  locationId: raw.location_id,
  status: raw.status,
  startedAt: raw.started_at,
  endedAt: raw.ended_at ?? null,
  durationSeconds: raw.duration_seconds,
  alertedAt: raw.alerted_at ?? null,
  alertScope: raw.alert_scope ?? null,
  remindedAt: raw.reminded_at ?? null,
});

const toPage = (data: any): CursorPage<StatusPeriod> => ({
  content: (data?.content ?? []).map(toPeriod),
  nextCursor: data?.next_cursor ?? null,
});

/** Lists the entities currently holding a status. entity_type is always sent: the server defaults it to EVSE. */
export const listCurrentStatus = async ({
  entityType,
  status,
  search,
  sort,
  limit,
  cursor,
}: CurrentStatusOptions): Promise<CursorPage<StatusPeriod>> => {
  const params: Record<string, unknown> = { entity_type: entityType };
  if (status) params.status = status;
  const q = search?.trim();
  if (q) params.q = q;
  if (sort) params.sort = sort;
  if (limit !== undefined) params.limit = limit;
  if (cursor) params.cursor = cursor;

  const response = await stationOpsApi.get("/internal/station-status", { params });
  return toPage(response.data);
};

/** Reads one batch of status periods for a single entity, newest first. */
export const getStatusHistory = async (
  entityType: EntityType,
  entityId: string,
  { from, to, limit, cursor }: HistoryOptions = {},
): Promise<CursorPage<StatusPeriod>> => {
  const params: Record<string, unknown> = {};
  if (from) params.from = from;
  if (to) params.to = to;
  if (limit !== undefined) params.limit = limit;
  if (cursor) params.cursor = cursor;

  const response = await stationOpsApi.get(
    `/internal/station-status/${entityType}/${encodeURIComponent(entityId)}/history`,
    { params },
  );
  return toPage(response.data);
};
